import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalDropdown } from '../components/GlobalDropdown';
import { GlobalInput } from '../components/GlobalInput';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalPagination, {
  type PaginationActions,
  type PaginationState,
} from '../components/GlobalPagination';
import GlobalSidebar from '../components/GlobalSidebar';
import {
  ManualBankAccountForm,
  type ManualBankAccountFormValues,
} from '../components/ManualBankAccountForm';
import { StatCard } from '../components/StatCard';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import {
  BANK_TRANSACTION_CATEGORY_OPTIONS,
  bankTransactionCategoryLabel,
  updateBankTransactionCategory,
} from '../hooks/useBankWorkspaceState';
import { saveManualBankAccount } from '../lib/saveManualBankAccount';
import {
  syncBankTransactions,
  syncBankTransactionsErrorMessage,
} from '../lib/syncBankTransactions';
import type { BankTransaction } from '../types';
import { formatMoney, formatSignedEuro } from '../utils/formatters';
import { bankAccountKindLabel } from '../lib/bankAccount';
import { Favicon, normalizeWebsiteUrl } from '../utils/favicon';
import authStyles from '../pages/auth/authPages.module.css';
import styles from './BankAccountDetailView.module.css';
import '../App.css';

const TRANSACTIONS_PER_PAGE = 50;

type DateFilterPreset = '30d' | '90d' | 'ytd' | 'all';

const DATE_FILTER_OPTIONS: { value: DateFilterPreset; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All' },
];

function dateFilterCutoff(preset: DateFilterPreset): Date | null {
  if (preset === 'all') return null;

  const now = new Date();
  if (preset === 'ytd') {
    return new Date(now.getFullYear(), 0, 1);
  }

  const days = preset === '30d' ? 30 : 90;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  return cutoff;
}

function transactionMatchesDateFilter(tx: BankTransaction, preset: DateFilterPreset): boolean {
  const cutoff = dateFilterCutoff(preset);
  if (!cutoff) return true;

  const bookedAt = new Date(tx.bookedAt);
  if (Number.isNaN(bookedAt.getTime())) return false;
  return bookedAt >= cutoff;
}

function filterEmptyMessage(searchQuery: string, dateFilter: DateFilterPreset): string {
  const trimmedSearch = searchQuery.trim();
  const hasSearch = trimmedSearch.length > 0;
  const hasDateFilter = dateFilter !== 'all';

  if (hasSearch && hasDateFilter) {
    return 'No transactions match your search and date filter.';
  }
  if (hasSearch) {
    return `No transactions match "${trimmedSearch}".`;
  }
  if (dateFilter === '30d') {
    return 'No transactions in the last 30 days.';
  }
  if (dateFilter === '90d') {
    return 'No transactions in the last 90 days.';
  }
  if (dateFilter === 'ytd') {
    return 'No transactions year to date.';
  }
  return 'No transactions match your filters.';
}

function transactionMatchesSearch(tx: BankTransaction, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  const haystacks = [tx.counterpartyName, tx.label, tx.reference ?? ''].map((value) =>
    value.toLowerCase()
  );
  return haystacks.some((value) => value.includes(normalized));
}

function computeVisiblePages(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: number[] = [1];
  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  pages.push(totalPages);
  return Array.from(new Set(pages)).sort((a, b) => a - b);
}

function formatSyncSuccessMessage(transactionsSynced: number, stub: boolean): string {
  const countLabel =
    transactionsSynced === 1
      ? '1 transaction'
      : `${transactionsSynced.toLocaleString()} transactions`;
  const base = `Synced ${countLabel} successfully.`;
  return stub ? `${base} (development stub data)` : base;
}

function formatTransactionDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function amountClassName(amount: number): string {
  if (amount > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (amount < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return 'financialSnapshot-profit';
}

function transactionCategoryValue(category?: string): string {
  return category && category.trim() ? category : 'uncategorized';
}

export function BankAccountDetailView() {
  const { accountId } = useParams<{ accountId: string }>();
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { bankAccounts, bankConnections, bankTransactions, bankLoading } = useAppData();
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilterPreset>('all');
  const [categoryTransaction, setCategoryTransaction] = useState<BankTransaction | null>(null);
  const [categoryDraft, setCategoryDraft] = useState('uncategorized');
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const account = useMemo(
    () => (bankAccounts ?? []).find((item) => item.id === accountId) ?? null,
    [bankAccounts, accountId]
  );

  const connectionLastSyncAt = useMemo(() => {
    if (!account) return undefined;
    return (
      bankConnections?.find((connection) => connection.id === account.connectionId)
        ?.lastSyncAt ?? undefined
    );
  }, [account, bankConnections]);

  const accountTransactions = useMemo((): BankTransaction[] => {
    if (!accountId || !bankTransactions) return [];
    return bankTransactions
      .filter((tx) => tx.bankAccountId === accountId)
      .sort((a, b) => new Date(b.bookedAt).getTime() - new Date(a.bookedAt).getTime());
  }, [accountId, bankTransactions]);

  const filteredTransactions = useMemo(() => {
    return accountTransactions.filter(
      (tx) =>
        transactionMatchesDateFilter(tx, dateFilter) && transactionMatchesSearch(tx, searchQuery)
    );
  }, [accountTransactions, dateFilter, searchQuery]);

  const totalTransactions = filteredTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalTransactions / TRANSACTIONS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [accountId, dateFilter, searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * TRANSACTIONS_PER_PAGE;
    return filteredTransactions.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [filteredTransactions, currentPage]);

  const paginationState = useMemo((): PaginationState => {
    return {
      currentPage,
      totalPages,
      totalItems: totalTransactions,
      itemsPerPage: TRANSACTIONS_PER_PAGE,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      visiblePages: computeVisiblePages(currentPage, totalPages),
    };
  }, [currentPage, totalPages, totalTransactions]);

  const paginationActions = useMemo((): PaginationActions => {
    return {
      goToPage: (page: number) => {
        setCurrentPage(Math.min(Math.max(1, page), totalPages));
      },
      nextPage: () => {
        setCurrentPage((page) => Math.min(page + 1, totalPages));
      },
      previousPage: () => {
        setCurrentPage((page) => Math.max(page - 1, 1));
      },
      firstPage: () => {
        setCurrentPage(1);
      },
      lastPage: () => {
        setCurrentPage(totalPages);
      },
    };
  }, [totalPages]);

  const rangeStart =
    totalTransactions === 0 ? 0 : (currentPage - 1) * TRANSACTIONS_PER_PAGE + 1;
  const rangeEnd = Math.min(currentPage * TRANSACTIONS_PER_PAGE, totalTransactions);

  async function handleSync() {
    if (!account || syncing) return;

    setSyncError(null);
    setSyncSuccess(null);
    setSyncing(true);

    try {
      const result = await syncBankTransactions(account.id);
      setSyncSuccess(formatSyncSuccessMessage(result.transactionsSynced, result.stub === true));
    } catch (err) {
      setSyncError(syncBankTransactionsErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  function openCategorySidebar(transaction: BankTransaction) {
    setCategoryError(null);
    setCategoryTransaction(transaction);
    setCategoryDraft(transactionCategoryValue(transaction.category));
  }

  function closeCategorySidebar() {
    if (categorySaving) return;
    setCategoryTransaction(null);
    setCategoryError(null);
  }

  async function handleSaveCategory() {
    if (!user?.uid || !categoryTransaction || categorySaving) return;

    setCategorySaving(true);
    setCategoryError(null);

    try {
      await updateBankTransactionCategory(
        user.uid,
        categoryTransaction.id,
        categoryDraft
      );
      setCategoryTransaction(null);
      setCategoryError(null);
    } catch (err) {
      setCategoryError(
        err instanceof Error ? err.message : 'Failed to save category. Please try again.'
      );
    } finally {
      setCategorySaving(false);
    }
  }

  function openEditSidebar() {
    setEditError(null);
    setEditOpen(true);
  }

  function closeEditSidebar() {
    if (editSaving) return;
    setEditOpen(false);
    setEditError(null);
  }

  async function handleSaveAccount(values: ManualBankAccountFormValues) {
    if (!user?.uid || !account || editSaving) return;

    setEditError(null);
    setEditSaving(true);

    try {
      await saveManualBankAccount(user.uid, {
        ...values,
        id: account.id,
        existing: { createdAt: account.createdAt, provider: account.provider },
      });
      setEditOpen(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save bank account.');
    } finally {
      setEditSaving(false);
    }
  }

  if (bankLoading) {
    return (
      <ViewPage title="Bank account" backTo="/bank-accounts" backLabel="Back to bank accounts">
        <section>
          <p className="muted">Loading bank data…</p>
        </section>
      </ViewPage>
    );
  }

  if (!accountId || !account) {
    return (
      <ViewPage title="Bank account" backTo="/bank-accounts" backLabel="Back to bank accounts">
        <section>
          <p className="muted">Bank account not found.</p>
        </section>
      </ViewPage>
    );
  }

  const isManualAccount = account.provider === 'manual';
  const lastSyncAt = account.balanceUpdatedAt ?? connectionLastSyncAt;
  const lastSyncLabel = isManualAccount
    ? lastSyncAt
      ? new Date(lastSyncAt).toLocaleString('de-DE', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'Manually tracked'
    : lastSyncAt
      ? new Date(lastSyncAt).toLocaleString('de-DE', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'Not synced yet';

  const pencilIcon = (
    <img
      src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const toolbarActions = isManualAccount ? (
    <GlobalButton
      type="button"
      variant="secondary"
      className={styles.tapTarget}
      onClick={openEditSidebar}
      icon={pencilIcon}
    >
      Edit bank account
    </GlobalButton>
  ) : (
    <GlobalButton
      type="button"
      variant="primary"
      className={styles.tapTarget}
      isLoading={syncing}
      disabled={syncing}
      onClick={() => handleSync()}
    >
      Sync now
    </GlobalButton>
  );

  const websiteHref = normalizeWebsiteUrl(account.websiteUrl);
  let websiteHost: string | null = null;
  if (websiteHref) {
    try {
      websiteHost = new URL(websiteHref).hostname;
    } catch {
      websiteHost = null;
    }
  }

  const accountIdentifier =
    account.accountNumber?.trim() ||
    account.ibanMasked ||
    (isManualAccount ? '' : 'IBAN pending');
  const headerSubtitle = [bankAccountKindLabel(account.kind), accountIdentifier]
    .filter(Boolean)
    .join(' · ');

  return (
    <ViewPage
      title={account.name}
      backTo="/bank-accounts"
      backLabel="Back to bank accounts"
      actions={toolbarActions}
    >
      <ViewSectionHeader
        eyebrow="Banking"
        title={account.name}
        subtitle={headerSubtitle}
      >
        {account.websiteUrl || websiteHost ? (
          <div className="companyCell" style={{ marginTop: 8 }}>
            {account.websiteUrl ? (
              <Favicon
                websiteUrl={account.websiteUrl}
                size={20}
                alt=""
                className="companyFavicon"
              />
            ) : null}
            {websiteHref && websiteHost ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="detailWebsiteLink"
              >
                {websiteHost}
              </a>
            ) : null}
          </div>
        ) : null}
      </ViewSectionHeader>

      <section className="grid-stats grid-stats-2" aria-label="Account summary">
        <StatCard
          netWorthHeadline
          label="Balance"
          emoji="🏦"
          value={formatMoney(account.balance, account.currency)}
          hint={`${isManualAccount ? 'Updated' : 'Last sync'} ${lastSyncLabel}`}
          valueWrap
        />
        <StatCard
          label="Type"
          emoji="🗂️"
          value={bankAccountKindLabel(account.kind)}
          hint={isManualAccount ? 'Manual account' : 'Connected via bank'}
        />
        <StatCard
          label="Currency"
          emoji="💱"
          value={account.currency}
          hint={
            account.accountNumber?.trim()
              ? `No. ${account.accountNumber.trim()}`
              : account.ibanMasked || 'No account number'
          }
          valueWrap
        />
        <StatCard
          label={isManualAccount ? 'Updated' : 'Last sync'}
          emoji="🕒"
          value={lastSyncLabel}
          hint={
            isManualAccount
              ? 'Manual accounts are tracked by hand'
              : 'Most recent sync from your bank'
          }
          valueWrap
        />
      </section>

      {syncSuccess ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerSuccess} ${styles.banner}`}
          role="status"
        >
          {syncSuccess}
        </div>
      ) : null}

      {syncError ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
          role="alert"
        >
          <p style={{ margin: 0 }}>{syncError}</p>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            className={styles.tapTarget}
            disabled={syncing}
            isLoading={syncing}
            onClick={() => handleSync()}
            style={{ marginTop: 10 }}
          >
            Try again
          </GlobalButton>
        </div>
      ) : null}

      <ViewSectionHeader
        eyebrow="Banking"
        title="Transactions"
        subtitle={
          isManualAccount
            ? 'Manual accounts do not sync transactions.'
            : 'Activity imported from your connected bank.'
        }
      />

      <section className={styles.transactions} aria-label="Transactions">
        {accountTransactions.length === 0 ? (
          isManualAccount ? (
            <GlobalEmptyState />
          ) : (
            <GlobalEmptyState buttonText="Sync now" onButtonClick={() => handleSync()} />
          )
        ) : (
          <>
            <div className={styles.filters}>
              <div className={styles.dateFilters} role="tablist" aria-label="Filter by date">
                {DATE_FILTER_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={dateFilter === value}
                    className={`${styles.dateFilterButton} ${
                      dateFilter === value ? styles.dateFilterButtonActive : ''
                    }`}
                    onClick={() => setDateFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className={styles.searchWrap}>
                <GlobalInput
                  label="Search transactions"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Counterparty, label, or reference"
                  autoComplete="off"
                />
              </div>
            </div>

            {filteredTransactions.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                {filterEmptyMessage(searchQuery, dateFilter)}
              </p>
            ) : (
              <>
            <p className={`muted ${styles.rangeSummary}`}>
              {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{' '}
              {totalTransactions.toLocaleString()}
            </p>

            <div className="table-card assetRegistryListDesktop">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Counterparty</th>
                    <th>Label</th>
                    <th>Category</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{formatTransactionDate(tx.bookedAt)}</td>
                      <td>{tx.counterpartyName || '—'}</td>
                      <td>{tx.label || '—'}</td>
                      <td>
                        <span className={tx.category ? undefined : 'muted'}>
                          {bankTransactionCategoryLabel(tx.category)}
                        </span>
                      </td>
                      <td className={amountClassName(tx.amount)} style={{ textAlign: 'right' }}>
                        {formatSignedEuro(tx.amount)}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <GlobalButton
                          type="button"
                          variant="secondary"
                          autoWidth
                          className={styles.tapTarget}
                          onClick={() => openCategorySidebar(tx)}
                        >
                          Set category
                        </GlobalButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="assetRegistryListMobile" role="list">
              {paginatedTransactions.map((tx) => (
                <div
                  key={tx.id}
                  className={`assetRegistryCard ${styles.transactionCard}`}
                  role="listitem"
                >
                  <div className="assetRegistryCardTop">
                    <div className="assetRegistryCardMain">
                      <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                        {formatTransactionDate(tx.bookedAt)}
                      </p>
                      <h3 className="assetRegistryCardName" style={{ marginTop: 4 }}>
                        {tx.counterpartyName || '—'}
                      </h3>
                      <p className="assetRegistryCardCategory muted">{tx.label || '—'}</p>
                      <p className="muted" style={{ margin: '8px 0 0', fontSize: '0.8125rem' }}>
                        {bankTransactionCategoryLabel(tx.category)}
                      </p>
                    </div>
                    <div className="assetRegistryCardValues">
                      <div className={`assetRegistryCardValue ${amountClassName(tx.amount)}`}>
                        {formatSignedEuro(tx.amount)}
                      </div>
                    </div>
                  </div>
                  <div className={styles.transactionCardAction}>
                    <GlobalButton
                      type="button"
                      variant="secondary"
                      autoWidth
                      className={styles.tapTarget}
                      onClick={() => openCategorySidebar(tx)}
                    >
                      Set category
                    </GlobalButton>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.paginationWrap}>
              <GlobalPagination
                pagination={paginationState}
                actions={paginationActions}
                compact
              />
            </div>
              </>
            )}
          </>
        )}
      </section>

      <GlobalSidebar isOpen={categoryTransaction !== null} onClose={closeCategorySidebar}>
        {categoryTransaction ? (
          <div className="sidebarFormStack">
            <h2 className="sidebarFormTitle">Transaction category</h2>
            <p className="muted" style={{ margin: '0 0 4px' }}>
              {categoryTransaction.counterpartyName || '—'}
            </p>
            <p className="muted" style={{ margin: '0 0 16px', fontSize: '0.875rem' }}>
              {formatTransactionDate(categoryTransaction.bookedAt)} ·{' '}
              {formatSignedEuro(categoryTransaction.amount)}
            </p>
            <GlobalDropdown
              label="Category"
              value={categoryDraft}
              onChange={setCategoryDraft}
              options={BANK_TRANSACTION_CATEGORY_OPTIONS}
            />
            {categoryError ? (
              <p className="form-error" role="alert" style={{ marginTop: 12 }}>
                {categoryError}
              </p>
            ) : null}
            <div className="sidebarFormActions">
              <GlobalButton
                type="button"
                variant="secondary"
                className={styles.tapTarget}
                onClick={closeCategorySidebar}
                disabled={categorySaving}
              >
                Cancel
              </GlobalButton>
              <GlobalButton
                type="button"
                variant="primary"
                className={styles.tapTarget}
                isLoading={categorySaving}
                disabled={categorySaving}
                onClick={() => handleSaveCategory()}
              >
                Save
              </GlobalButton>
            </div>
          </div>
        ) : null}
      </GlobalSidebar>

      <GlobalSidebar isOpen={editOpen} onClose={closeEditSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit bank account</h2>
          <p className="bankProviderConnectIntro">
            Update the name, website, balance, and IBAN for this manual account.
          </p>
          <ManualBankAccountForm
            key={account.id}
            account={account}
            saving={editSaving}
            error={editError}
            submitLabel="Save changes"
            onSubmit={handleSaveAccount}
            onCancel={closeEditSidebar}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
